import React, { useState, useEffect } from 'react';
import { TextField } from '../components/TextField';
import { Button } from '../components/Button';

interface CalculatorInputs {
  callsPerMonth: number;
  missedCallPercentage: number;
  averageTicket: number;
  grossMarginPercentage: number;
  recoveryPercentage: number;
  csrCapacity: number;
  csrCost: number;
  syntraPrice: number;
}

interface CalculatorResults {
  annualRevenueLost: number;
  annualEbitdaLost: number;
  annualEbitdaRecovered: number;
  csrPayrollCost: number;
  syntraCost: number;
  netEbitdaImpact: number;
}

const Calculator: React.FC = () => {
  // Default input values
  const defaultInputs: CalculatorInputs = {
    callsPerMonth: 2400,
    missedCallPercentage: 18,
    averageTicket: 225,
    grossMarginPercentage: 35,
    recoveryPercentage: 50,
    csrCapacity: 600,
    csrCost: 4000,
    syntraPrice: 2.5,
  };

  // State for inputs and results
  const [inputs, setInputs] = useState<CalculatorInputs>(defaultInputs);
  const [results, setResults] = useState<CalculatorResults>({
    annualRevenueLost: 0,
    annualEbitdaLost: 0,
    annualEbitdaRecovered: 0,
    csrPayrollCost: 0,
    syntraCost: 0,
    netEbitdaImpact: 0,
  });

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numValue = value === '' ? 0 : parseFloat(value);
    
    setInputs(prev => ({
      ...prev,
      [name]: numValue,
    }));
  };

  // Calculate results whenever inputs change
  useEffect(() => {
    const {
      callsPerMonth,
      missedCallPercentage,
      averageTicket,
      grossMarginPercentage,
      recoveryPercentage,
      csrCapacity,
      csrCost,
      syntraPrice,
    } = inputs;

    // Calculate annual revenue lost
    const annualRevenueLost = callsPerMonth * (missedCallPercentage / 100) * averageTicket * 12;
    
    // Calculate annual EBITDA lost
    const annualEbitdaLost = annualRevenueLost * (grossMarginPercentage / 100);
    
    // Calculate annual EBITDA recovered
    const annualEbitdaRecovered = annualEbitdaLost * (recoveryPercentage / 100);
    
    // Calculate CSR payroll cost - using a more realistic approach
    // Instead of using Math.ceil which causes large jumps, we'll use a fractional approach
    // This assumes CSRs can work overtime or part-time to handle the workload
    const csrRatio = callsPerMonth / csrCapacity;
    const csrPayrollCost = csrRatio * csrCost * 12;
    
    // Calculate Syntra cost
    const syntraCost = callsPerMonth * 12 * syntraPrice;
    
    // Calculate net EBITDA impact
    const netEbitdaImpact = annualEbitdaRecovered - (syntraCost - csrPayrollCost);

    setResults({
      annualRevenueLost,
      annualEbitdaLost,
      annualEbitdaRecovered,
      csrPayrollCost,
      syntraCost,
      netEbitdaImpact,
    });
  }, [inputs]);

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Format percentage
  const formatPercentage = (value: number) => {
    return `${value}%`;
  };

  return (
    <div className="min-h-screen bg-[#0A1A35] text-white p-8">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">ROI Calculator</h1>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Inputs Section */}
          <div className="bg-[#132B52]/90 backdrop-blur-sm p-6 rounded-2xl border border-[#1E3A64]">
            <h2 className="text-xl font-semibold mb-6">Inputs</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Calls per month</label>
                <input
                  type="number"
                  name="callsPerMonth"
                  value={inputs.callsPerMonth}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border bg-[#0A1A35] border-[#1E3A64] text-white px-3.5 py-2 text-gray-900 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 sm:text-sm sm:leading-6"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Missed-call %</label>
                <input
                  type="number"
                  name="missedCallPercentage"
                  value={inputs.missedCallPercentage}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border bg-[#0A1A35] border-[#1E3A64] text-white px-3.5 py-2 text-gray-900 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 sm:text-sm sm:leading-6"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Average ticket $</label>
                <input
                  type="number"
                  name="averageTicket"
                  value={inputs.averageTicket}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border bg-[#0A1A35] border-[#1E3A64] text-white px-3.5 py-2 text-gray-900 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 sm:text-sm sm:leading-6"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Gross margin %</label>
                <input
                  type="number"
                  name="grossMarginPercentage"
                  value={inputs.grossMarginPercentage}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border bg-[#0A1A35] border-[#1E3A64] text-white px-3.5 py-2 text-gray-900 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 sm:text-sm sm:leading-6"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Syntra can handle X% of missed calls</label>
                <input
                  type="number"
                  name="recoveryPercentage"
                  value={inputs.recoveryPercentage}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border bg-[#0A1A35] border-[#1E3A64] text-white px-3.5 py-2 text-gray-900 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 sm:text-sm sm:leading-6"
                />
                <p className="text-xs text-gray-400 mt-1">Percentage of missed calls that Syntra can handle (100% means all missed calls are recovered)</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Calls a single CSR can handle per month</label>
                <input
                  type="number"
                  name="csrCapacity"
                  value={inputs.csrCapacity}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border bg-[#0A1A35] border-[#1E3A64] text-white px-3.5 py-2 text-gray-900 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 sm:text-sm sm:leading-6"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Fully-loaded cost per CSR per month</label>
                <input
                  type="number"
                  name="csrCost"
                  value={inputs.csrCost}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border bg-[#0A1A35] border-[#1E3A64] text-white px-3.5 py-2 text-gray-900 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 sm:text-sm sm:leading-6"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Syntra price per handled call</label>
                <input
                  type="number"
                  name="syntraPrice"
                  value={inputs.syntraPrice}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border bg-[#0A1A35] border-[#1E3A64] text-white px-3.5 py-2 text-gray-900 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 sm:text-sm sm:leading-6"
                />
              </div>
            </div>
          </div>
          
          {/* Results Section */}
          <div className="bg-[#132B52]/90 backdrop-blur-sm p-6 rounded-2xl border border-[#1E3A64]">
            <h2 className="text-xl font-semibold mb-6">Results</h2>
            
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-300">Annual revenue lost</p>
                  <p className="text-xl font-semibold">{formatCurrency(results.annualRevenueLost)}</p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-300">Annual EBITDA lost</p>
                  <p className="text-xl font-semibold">{formatCurrency(results.annualEbitdaLost)}</p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-300">Annual EBITDA recovered</p>
                  <p className="text-xl font-semibold">{formatCurrency(results.annualEbitdaRecovered)}</p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-300">CSR payroll cost</p>
                  <p className="text-xl font-semibold">{formatCurrency(results.csrPayrollCost)}</p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-300">Syntra cost</p>
                  <p className="text-xl font-semibold">{formatCurrency(results.syntraCost)}</p>
                </div>
              </div>
              
              <div className="pt-4 border-t border-[#1E3A64]">
                <p className="text-sm text-gray-300 mb-2">Net EBITDA impact</p>
                <p className={`text-3xl font-bold ${results.netEbitdaImpact >= 0 ? 'text-emerald-400' : 'text-rose-500'}`}>
                  {formatCurrency(results.netEbitdaImpact)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Calculator; 